import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, GripHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

// Drag context — DialogHeader binds the drag handlers, DialogContent's
// inner box applies the offset to its transform. Each Dialog mount gets
// its own context so multiple dialogs don't collide.
type DragContextValue = {
  bindHandle: () => {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
  };
};
const DialogDragContext = React.createContext<DragContextValue | null>(null);

/**
 * Hook that produces drag offset state + pointer handlers. Used internally
 * by DialogContent. Bound to the title/header by DialogHeader so the user
 * grabs from the top bar (matches macOS/Windows window-drag convention).
 *
 * Uses pointer events + setPointerCapture so the drag continues even if
 * the cursor leaves the handle. Clamps the offset so the dialog can't be
 * dragged completely off-screen (always keeps ≥40px of the title visible).
 */
function useDialogDrag() {
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const startRef = React.useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  const onPointerDown = React.useCallback((e: React.PointerEvent) => {
    // Don't start a drag from interactive children (close button, links,
    // form fields). The header is mostly text + the title, but the close
    // button in particular sits right next to it.
    const target = e.target as HTMLElement | null;
    if (target?.closest("button, a, input, textarea, select")) return;
    startRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    document.body.style.userSelect = "none";
  }, [offset.x, offset.y]);

  const onPointerMove = React.useCallback((e: React.PointerEvent) => {
    if (!startRef.current) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    // Clamp so the title bar never goes fully off-screen — keep at least
    // 40px of the dialog edge visible in every direction.
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const nextX = Math.max(-vw / 2 + 40, Math.min(vw / 2 - 40, startRef.current.ox + dx));
    const nextY = Math.max(-vh / 2 + 40, Math.min(vh / 2 - 40, startRef.current.oy + dy));
    setOffset({ x: nextX, y: nextY });
  }, []);

  const onPointerUp = React.useCallback((e: React.PointerEvent) => {
    if (!startRef.current) return;
    startRef.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* already released */ }
    document.body.style.userSelect = "";
  }, []);

  const bindHandle = React.useCallback(() => ({ onPointerDown, onPointerMove, onPointerUp }), [onPointerDown, onPointerMove, onPointerUp]);

  return { offset, bindHandle };
}

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: "sm" | "md" | "lg" | "xl";
    hideClose?: boolean;
  }
>(({ className, children, size = "md", hideClose, ...props }, ref) => {
  const sizes = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-2xl", xl: "max-w-4xl" };
  const { offset, bindHandle } = useDialogDrag();
  return (
    <DialogPortal>
      <DialogOverlay />
      {/* Outer Content is a fullscreen flex container that centers the
          dialog box. Padding ensures the box never touches viewport edges.
          Animations stay on this layer. */}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed inset-0 z-50 flex items-center justify-center p-4 outline-none sm:p-6",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        )}
        {...props}
      >
        {/* Inner box: width-capped, height-capped to fit viewport minus
            outer padding, and the only scroll container. Translates by
            the drag offset so the user can move the window around. */}
        <div
          className={cn(
            "relative flex w-full flex-col max-h-[calc(100vh-3rem)]",
            "overflow-y-auto dialog-scroll",
            "rounded-lg border border-line bg-surface-panel p-6 shadow-lg",
            sizes[size],
            className,
          )}
          style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
        >
          <DialogDragContext.Provider value={{ bindHandle }}>
            {children}
          </DialogDragContext.Provider>
          {!hideClose && (
            <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded p-1 text-ink-dim opacity-70 transition-opacity hover:bg-surface-hover hover:opacity-100">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = "DialogContent";

/**
 * DialogHeader is also the drag handle. Hover shows grab cursor; mousedown
 * starts the drag (handled internally via DialogDragContext). Click on the
 * close button or a child link still works — the drag handler bails out
 * for interactive elements (see useDialogDrag.onPointerDown).
 *
 * Tiny grip glyph at the top-left signals the bar is grabbable.
 */
export const DialogHeader = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const ctx = React.useContext(DialogDragContext);
  const handlers = ctx?.bindHandle();
  return (
    <div
      className={cn(
        "relative -mx-6 -mt-6 mb-4 flex flex-col gap-1 px-6 pt-6 pb-3",
        // Subtle title-bar styling so it reads as a draggable area.
        ctx && "cursor-grab select-none border-b border-line bg-surface-panel2/40 active:cursor-grabbing",
        className,
      )}
      {...handlers}
      {...props}
    >
      {ctx && (
        <GripHorizontal className="absolute left-1/2 top-1.5 h-3 w-3 -translate-x-1/2 text-ink-dim/60" aria-hidden />
      )}
      {children}
    </div>
  );
};

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("font-display text-lg font-semibold text-ink", className)}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-ink-muted", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";
